import React, { useContext, useEffect, useMemo, useState } from 'react';
import '../styles/TtnContainer.css';
import { Context } from '../index';
import { WsTriggers } from '../WebsocketTriggers';
import { REACT_APP_API_URL } from '../config';
import { formatDateString } from '../utils/utilsFunctions';
import { setCookie } from '../utils/cookies';
import { ttnGetAPI, ttnGetByIdAPI, ttnDeleteAPI, ttnCreatePdfAPI } from '../http/ttnAPI';
import { payeesGetApi } from '../http/payeeAPI';
import { TtnFields } from '../utils/ttnTemplate';
import TtnForm, { TtnEntity } from './TtnForm';
import TtnStampPlacer, { StampPos } from './TtnStampPlacer';

// Запасное положение печати/подписи, если сервер почему-то не прислал stampPos
// (обычно приходит — при null отдаётся дефолт). Совпадает с DEFAULT_STAMP_POS
// на сервере (src/services/ttnStamp.ts).
const FALLBACK_STAMP_POS: StampPos = {
    stamp: { cxFrac: 0.27, cyFrac: 0.205, widthPt: 120 },
    sign: { cxFrac: 0.235, cyFrac: 0.225, widthPt: 95 },
};

type CompLite = {
    id: number;
    name: string;
    code: string | null;
    address: string | null;
    payee?: { id: number; name: string } | null;
} | null;

type PayeeLite = { id: number; name: string; isDefault?: boolean };

type Props = {
    compData: CompLite;
    setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
    setHeaderName: React.Dispatch<React.SetStateAction<string>>;
    setBodyDivElement: React.Dispatch<React.SetStateAction<JSX.Element>>;
};

const fileHref = (path: string) => `${REACT_APP_API_URL}media/${path}`;

// Через target=_blank бекенд не побачить Authorization-хедер — той самий трюк,
// що і для pdf розрахунків: кладемо JWT у куку перед відкриттям.
const stashJwtCookie = () => {
    const jwt = localStorage.getItem('token');
    if (jwt) setCookie('cookieJwt', jwt, 7);
};

const TtnContainer: React.FC<Props> = ({ compData, setIsActive, setHeaderName, setBodyDivElement }) => {
    const { user, ws } = useContext(Context);

    const companyId = window.location.pathname.split('/').pop() as string;

    const [items, setItems] = useState<TtnEntity[]>([]);
    const [loading, setLoading] = useState(false);
    const [payees, setPayees] = useState<PayeeLite[]>([]);
    // id получателя платежа для фильтра списка, либо 'all'. null — ещё не решили
    // (ждём payees / compData), тогда покажем для «текущего» получателя.
    const [filterPayeeId, setFilterPayeeId] = useState<number | 'all' | null>(null);

    // Права ttn.* ще не заведені в каталозі дозволів бекенду. Поки жодного
    // ttn.*-дозволу немає в наборі взагалі — показуємо всі дії (сам ендпоінт
    // api/v1/ttn перевіряє доступ на сервері). Щойно бекенд почне видавати
    // ttn.add / ttn.update / ttn.delete — гейтинг вмикається автоматично.
    const perms: Set<string> = user.getPermissions;
    const ttnPermsProvisioned =
        perms.has('ttn.add') || perms.has('ttn.update') || perms.has('ttn.delete');
    const canAdd = !ttnPermsProvisioned || perms.has('ttn.add');
    const canUpdate = !ttnPermsProvisioned || perms.has('ttn.update');
    const canDelete = !ttnPermsProvisioned || perms.has('ttn.delete');

    // «Текущий» получатель для компании: выбранный у неё, иначе — по умолчанию.
    // К нему же сервер привязывает новую ТТН, и на нём же по умолчанию стоит
    // фильтр списка.
    const currentPayeeId: number | null = useMemo(() => {
        if (compData?.payee?.id) return compData.payee.id;
        const def = payees.find((p) => p.isDefault);
        return def ? def.id : null;
    }, [compData, payees]);

    useEffect(() => {
        payeesGetApi().then((res: any) => {
            if (Array.isArray(res?.data)) setPayees(res.data as PayeeLite[]);
        });
    }, []);

    // Инициализируем фильтр «текущим» получателем, как только он известен.
    useEffect(() => {
        if (filterPayeeId === null && (currentPayeeId !== null || payees.length > 0)) {
            setFilterPayeeId(currentPayeeId ?? 'all');
        }
    }, [currentPayeeId, payees.length, filterPayeeId]);

    const requestList = () => {
        if (!companyId || filterPayeeId === null) return;
        setLoading(true);
        ttnGetAPI(companyId, filterPayeeId === 'all' ? undefined : filterPayeeId)
            .then((res: any) => {
                if (Array.isArray(res?.data)) setItems(res.data);
            })
            .finally(() => setLoading(false));
    };

    useEffect(requestList, [companyId, filterPayeeId]);

    useEffect(() => {
        const message = ws.getMsg;
        if (message && message === WsTriggers.TTN + `+${companyId}`) {
            requestList();
            ws.setMessage(null);
        }
    }, [ws.getMsg]);

    const prefillFromCompany = (): Partial<TtnFields> => {
        const p: Partial<TtnFields> = {};
        if (compData) {
            p.consigneeName = compData.name || '';
            p.consigneeCode = compData.code || '';
            p.unloadingPoint = compData.address || '';
            if (compData.payee?.name) {
                p.shipperName = compData.payee.name;
                p.carrier = compData.payee.name;
            }
        }
        return p;
    };

    const closePopup = () => setIsActive(false);

    const openForm = (existing: TtnEntity | null) => {
        setHeaderName(existing ? `ТТН ${existing.number || ''}`.trim() : 'Новая ТТН');
        setBodyDivElement(
            <TtnForm
                companyId={companyId}
                userId={user.getUser.id}
                prefill={existing ? undefined : prefillFromCompany()}
                existing={existing}
                readOnly={!!existing && !canUpdate}
                onSaved={() => {
                    closePopup();
                    // Новая ТТН привязана к «текущему» получателю — переключаем
                    // на него фильтр, чтобы она была видна (иначе refetch по WS).
                    setFilterPayeeId(currentPayeeId ?? 'all');
                    requestList();
                }}
                onCancel={closePopup}
            />,
        );
        setIsActive(true);
    };

    const openById = (id: number) => {
        ttnGetByIdAPI(id).then((res: any) => {
            if (res?.data?.id) openForm(res.data as TtnEntity);
        });
    };

    const remove = (id: number) => {
        if (!window.confirm('Удалить эту ТТН?')) return;
        ttnDeleteAPI(id).then((res: any) => {
            if (res?.status === 200) requestList();
        });
    };

    const regenPdf = (id: number) => ttnCreatePdfAPI(id).then(requestList);

    // Открываем модалку интерактивного размещения печати/подписи на текущем PDF.
    const openStamped = (t: TtnEntity) => {
        setHeaderName(`Печать и подпись — ТТН ${t.number || ''}`.trim());
        setBodyDivElement(
            <TtnStampPlacer
                ttnId={t.id}
                pdfFile={t.pdfFile as string}
                stampFile={t.payee?.stampFile ?? null}
                signFile={t.payee?.signFile ?? null}
                initial={t.stampPos ?? FALLBACK_STAMP_POS}
                onDone={() => {
                    setIsActive(false);
                    requestList();
                }}
                onCancel={() => setIsActive(false)}
            />,
        );
        setIsActive(true);
    };

    return (
        <div className="contentWindow ttn__container">
            <div className="selectedComp__data-header contentWindow__header">
                <span className="contentWindow__name">Товарно-транспортные накладные</span>
            </div>
            <nav className="selectedComp__data-nav contentWindow__nav">
                {canAdd && (
                    <button
                        className="nav__button btn-primary"
                        disabled={!compData}
                        onClick={() => openForm(null)}
                    >
                        Создать ТТН
                    </button>
                )}
                <label className="ttn__payee-filter">
                    <span className="ttn__payee-filter-label">Получатель:</span>
                    <select
                        className="input ttn__payee-select"
                        value={filterPayeeId === null ? '' : String(filterPayeeId)}
                        onChange={(e) =>
                            setFilterPayeeId(e.target.value === 'all' ? 'all' : Number(e.target.value))
                        }
                    >
                        <option value="all">Все получатели</option>
                        {payees.map((p) => (
                            <option key={`fp${p.id}`} value={p.id}>
                                {p.name}
                                {p.id === currentPayeeId ? ' (текущий)' : ''}
                            </option>
                        ))}
                    </select>
                </label>
            </nav>
            <div className="selectedComp__calc-data-body contentWindow__body">
                {loading && items.length === 0 ? (
                    <div className="ttn__empty">Загрузка…</div>
                ) : items.length === 0 ? (
                    <div className="ttn__empty">Пока нет ни одной ТТН</div>
                ) : (
                    items.map((t) => {
                        const canStamp =
                            !!t.pdfFile && !!t.payee && (t.payee.hasStamp || t.payee.hasSign);
                        return (
                            <div key={`ttn${t.id}`} className="ttn__row">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="ttn__td-static">№</td>
                                            <td className="ttn__td-dynamic">{t.number || '—'}</td>
                                            <td className="ttn__td-static">Дата</td>
                                            <td className="ttn__td-dynamic">{t.date || '—'}</td>
                                            <td className="ttn__td-static">Создал</td>
                                            <td className="ttn__td-dynamic">{t.createdBy?.username || '—'}</td>
                                            <td className="ttn__td-dynamic">
                                                {t.createdAt ? formatDateString(t.createdAt) : ''}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="ttn__td-static">Получатель</td>
                                            <td className="ttn__td-dynamic" colSpan={6}>
                                                {t.payee?.name || <span className="ttn__muted">—</span>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="ttn__td-static">Файлы</td>
                                            <td className="ttn__td-dynamic" colSpan={3}>
                                                {t.xlsxFile ? (
                                                    <a href={fileHref(t.xlsxFile)} target="_blank" rel="noreferrer"
                                                       onClick={stashJwtCookie}>xlsx</a>
                                                ) : (
                                                    <span className="ttn__muted">xlsx —</span>
                                                )}
                                                {'  '}
                                                {t.pdfFile ? (
                                                    <a href={fileHref(t.pdfFile)} target="_blank" rel="noreferrer"
                                                       onClick={stashJwtCookie}>pdf</a>
                                                ) : (
                                                    <span className="ttn__muted">pdf —</span>
                                                )}
                                            </td>
                                            <td className="ttn__td-actions" colSpan={3}>
                                                <button className="nav__button btn-ghost" onClick={() => openById(t.id)}>
                                                    {canUpdate ? 'Открыть' : 'Просмотреть'}
                                                </button>
                                                {canUpdate && (
                                                    <button className="nav__button btn-ghost" onClick={() => regenPdf(t.id)}>
                                                        Пересоздать pdf
                                                    </button>
                                                )}
                                                <button
                                                    className="nav__button btn-ghost"
                                                    disabled={!canStamp}
                                                    title={
                                                        !t.pdfFile
                                                            ? 'Сначала сформируйте PDF накладной'
                                                            : !canStamp
                                                                ? 'У получателя платежа не заданы печать и подпись'
                                                                : 'Скачать PDF с печатью и подписью получателя платежа'
                                                    }
                                                    onClick={() => openStamped(t)}
                                                >
                                                    Печать и подпись
                                                </button>
                                                {canDelete && (
                                                    <button className="nav__button btn-danger" onClick={() => remove(t.id)}>
                                                        Удалить
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default TtnContainer;
